import React, { memo, useCallback, useEffect, useState } from 'react'
import { Typography } from '@mui/material'
import tw from 'twin.macro'
import { useDispatch, useSelector } from 'react-redux'
import { createAsyncAction } from 'src/utils/reduxUtils'
import {
  DeleteRejectionReason,
  FetchRejectionReasons,
  FetchRejectionReasonsToDecline,
} from 'src/constants/actionTypes'
import { handleApiError } from 'src/utils/errorHandlers'
import { colors } from 'src/theme'
import { CirclePlusMajor, CircleCancelMinor } from '@shopify/polaris-icons'
import { Button } from 'src/components/shared/Button'
import { showToast } from 'src/utils/toast'
import FailedImage from 'src/assets/images/failed.png'
import { AddEditDeclineReasonModal } from './AddEditDeclineReasonModal'
import { useModalOpen } from 'src/hooks/useModalOpen'
import { RejectionReason } from 'src/interfaces/rejectionReason'
import {
  getCustomRejectionReasons,
  getDefaultRejectionReasons,
} from 'src/selectors/rejectionReason'
import { SensitiveActionModal } from '../shared/SensitiveActionModal'
import UnassignImage from 'src/assets/images/unassign.svg'
import { GSIcon } from '../shared/GSIcon'
import dayjs from 'dayjs'
import { UserPermission } from 'src/constants/user'
import useCheckPermissions from 'src/hooks/useCheckPermissions'

const DeclineReasonsSectionComponent = () => {
  const dispatch = useDispatch()

  const { hasPermission } = useCheckPermissions()

  const [editingReason, setEditingReason] = useState<RejectionReason | null>(null)
  const [deletingReason, setDeletingReason] = useState<RejectionReason | null>(null)

  const defaultReasons = useSelector(getDefaultRejectionReasons)
  const customReasons = useSelector(getCustomRejectionReasons)

  const { isOpen: isAddModalOpen, open: openAddModal, close: closeAddModal } = useModalOpen()
  const {
    isOpen: isDeleteConfirmOpen,
    open: openDeleteConfirm,
    close: closeDeleteConfirm,
  } = useModalOpen()

  const handleFetchReasons = useCallback(async () => {
    try {
      await createAsyncAction<RejectionReason[], void>(dispatch, FetchRejectionReasons.request())
      await createAsyncAction<RejectionReason[], void>(
        dispatch,
        FetchRejectionReasonsToDecline.request(),
      )
    } catch (err) {
      handleApiError(err)
    }
  }, [dispatch])

  useEffect(() => {
    handleFetchReasons()
  }, [handleFetchReasons])

  const handleCreateReason = useCallback(() => {
    setEditingReason(null)
    openAddModal()
  }, [openAddModal])

  const handleEditReason = useCallback(
    (reason: RejectionReason) => {
      setEditingReason(reason)
      openAddModal()
    },
    [openAddModal],
  )

  const handleCloseDeleteConfirm = useCallback(() => {
    setDeletingReason(null)
    closeDeleteConfirm()
  }, [])

  const handleOpenDeleteConfirm = useCallback((reason: RejectionReason) => {
    setDeletingReason(reason)
    openDeleteConfirm()
  }, [])

  const handleDeleteReason = useCallback(async () => {
    if (!deletingReason) {
      return
    }

    try {
      await createAsyncAction(
        dispatch,
        DeleteRejectionReason.request({ reasonId: deletingReason?.id }),
      )

      await handleFetchReasons()

      handleCloseDeleteConfirm()

      showToast('Decline reason deleted.')
    } catch (err) {
      handleApiError(err)
    }
  }, [deletingReason, dispatch, handleFetchReasons, handleCloseDeleteConfirm])

  const handleCloseModal = useCallback(() => {
    setEditingReason(null)
    closeAddModal()
  }, [closeAddModal])

  return (
    <div css={tw`flex flex-col gap-4`}>
      <Typography css={tw`mb-2`} variant="h2">
        Decline reasons
      </Typography>
      <div css={tw`flex flex-col`}>
        <Typography variant="h4">Custom Decline Reasons</Typography>
        <div css={tw`border-0 border-b border-solid border-[#EDEDED] mt-2 mb-2`} />
        {hasPermission(UserPermission.CustomerReviewRejectionReasonEdit) &&
          customReasons.length > 0 && (
            <Button
              size="small"
              color="light"
              onClick={handleCreateReason}
              endIcon={<CirclePlusMajor height={20} width={20} fill={colors.PRIMARY_PURPLE_500} />}
              className={tw`w-full mt-2 mb-2`}
            >
              Add new reason
            </Button>
          )}
        {customReasons.map((reason) => (
          <div
            key={reason.id || reason.name}
            css={tw`bg-white rounded-lg p-4 border border-solid border-[#EDEDED] mt-2`}
          >
            <div css={tw`flex items-start justify-between mb-2`}>
              <div css={tw`flex-1`}>
                <Typography variant="h6">{reason.name}</Typography>
                {reason.createdAt && (
                  <Typography variant="body2" color={colors.SHADES_GREY_400}>
                    {dayjs(reason.createdAt * 1000).format('HH:mm DD/MM/YY')}
                  </Typography>
                )}
              </div>
              <div css={tw`flex gap-2`}>
                {hasPermission(UserPermission.CustomerReviewRejectionReasonEdit) && (
                  <Button
                    color="grey"
                    size="small"
                    className={tw`w-9 h-9`}
                    leftIcon={
                      <GSIcon iconName="gs_pencil" css={tw`self-center`} width={20} height={20} />
                    }
                    onClick={() => handleEditReason(reason)}
                  />
                )}
                {hasPermission(UserPermission.CustomerReviewRejectionReasonDelete) && (
                  <Button
                    color="grey"
                    size="small"
                    className={tw`w-9 h-9`}
                    leftIcon={<CircleCancelMinor width={20} />}
                    onClick={() => reason.id && handleOpenDeleteConfirm(reason)}
                  />
                )}
              </div>
            </div>
            {reason.message && (
              <>
                <div css={tw`border-0 border-b border-solid border-[#EDEDED] mt-4 mb-4`} />
                <Typography variant="body2" color={colors.SHADES_GREY_400}>
                  {reason.message}
                </Typography>
              </>
            )}
          </div>
        ))}

        {customReasons.length === 0 && (
          <div css={tw`bg-white rounded-lg p-4 border border-solid border-[#EDEDED] mt-2`}>
            <div css={tw`flex flex-col items-center justify-center py-8 px-4`}>
              <img
                src={FailedImage}
                alt="No custom decline reasons"
                css={tw`mb-4`}
                width={210}
                height={164}
              />
              <Typography variant="h4" css={tw`mb-2 font-semibold`}>
                No Custom Decline Reasons
              </Typography>
              <Typography variant="body2" color={colors.SHADES_GREY_400} css={tw`text-center`}>
                Add a file to start sharing documents, resources, or key attachments.
              </Typography>
              {hasPermission(UserPermission.CustomerReviewRejectionReasonEdit) && (
                <Button
                  size="small"
                  color="light"
                  onClick={handleCreateReason}
                  endIcon={
                    <CirclePlusMajor height={20} width={20} fill={colors.PRIMARY_PURPLE_500} />
                  }
                  className={tw`w-full mt-6`}
                >
                  Add new reason
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      <div css={tw`flex flex-col`}>
        <Typography variant="h4">Default Decline Reasons</Typography>
        {defaultReasons.length > 0 && (
          <div css={tw`border-0 border-b border-solid border-[#EDEDED] mb-2 mt-2`} />
        )}

        {defaultReasons.length === 0 ? (
          <Typography variant="body2" color={colors.SHADES_GREY_400} css={tw`py-4 text-center`}>
            No default decline reasons available.
          </Typography>
        ) : (
          defaultReasons.map((reason) => (
            <div
              key={reason.id}
              css={tw`bg-white rounded-lg p-4 mt-2 border border-solid border-[#EDEDED]`}
            >
              <Typography variant="h6" css={tw`mb-1`}>
                {reason.name}
              </Typography>
              <Typography variant="body2" color={colors.SHADES_GREY_400}>
                {reason.message}
              </Typography>
            </div>
          ))
        )}
      </div>
      <AddEditDeclineReasonModal
        isOpen={isAddModalOpen}
        initialReason={editingReason}
        onClose={handleCloseModal}
        onSave={handleFetchReasons}
      />
      <SensitiveActionModal
        isOpen={isDeleteConfirmOpen}
        title="Delete Reason"
        text={`Are you sure you want to delete “${deletingReason?.name || 'rejection reason'}”?`}
        submitLabel="Delete"
        image={<img src={UnassignImage} />}
        onClose={handleCloseDeleteConfirm}
        onSubmit={handleDeleteReason}
      />
    </div>
  )
}

export const DeclineReasonsSection = memo(DeclineReasonsSectionComponent)
